import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/button'
import { Box, Flex, Spinner } from 'components/elements'
import FormWrapper from 'features/auth/components/FormWrapper'
import { ErrorBox } from 'features/profile/components/EditProfileForm'
import { Controller, FieldError, useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { CoverageItem, CoverageLookup, FormData, QuoteProps, Service } from '../type'
import { ApiErrorCode, MESSAGE_ENUM, Stages, ValidationErrors } from 'utils/enum'
import { useNavigate } from 'react-router-dom'
import { Period, routes } from 'configs/constants'
import { CustomRadioButton, ErrorCSS, Form, Label, userRegHeadingCss } from 'views/style'
import _ from 'lodash'
import { useGetPlanListingForQuote } from 'features/quote/api/getPlanListingForQuote'
import { useEffect, useMemo, useState } from 'react'

import { QouteLayout } from 'features/quote/page/GetAQuotes'
import { AxiosError } from 'axios'
import { getUpperCase } from 'utils/function'
import { BackNavArrowIcon } from 'components/icons'
import { TERMINOLOGY } from 'utils/enum'

export const step2Schema = Yup.object().shape({
  period: Yup.string().required('Please select a payment period'),
  coverage: Yup.string().required(ValidationErrors.COVERAGE_PLAN),
})

const CoveragePlan = (props: QuoteProps) => {
  const navigate = useNavigate()
  const planId = props?.quoteUserReg?.planId
  // if planId is not available then return
  const { data: planList, isLoading, isError, error } = useGetPlanListingForQuote({ id: planId })

  const [submitError, setSubmitError] = useState('')
  const coverageRange = planList?.firstPlan?.coveragePlans
  //user selected plan details
  const planInfo = planList?.firstPlan

  if (coverageRange) {
    coverageRange.sort((a: { maxCoverage: number }, b: { maxCoverage: number }) => a.maxCoverage - b.maxCoverage)
  }
  const maxCoverageRange: Service[] = coverageRange?.map((item: { maxCoverage: string }) => {
    const coverage = parseFloat(item.maxCoverage).toLocaleString()

    // Format the number with thousands separators
    return {
      label: `$${coverage}`,
      value: `${item.maxCoverage}`,
    }
  })
  // remove the duplicates from maxCoverageRange (TODO: check on backend and remove from here)
  const uniqueMaxCoverageRange = maxCoverageRange?.reduce((acc: Service[], curr: Service) => {
    const existingItem = acc.find((item: Service) => item.value === curr.value)
    if (!existingItem) {
      acc.push(curr)
    }
    return acc
  }, [])

  const cLookup = useMemo<CoverageLookup>(() => {
    const map: CoverageLookup = {}
    const coveragePlans: CoverageItem[] = coverageRange ?? []

    coveragePlans.forEach(item => {
      map[item.maxCoverage] = item
    })

    return map
  }, [coverageRange])

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(step2Schema),
    defaultValues: {
      coverage: (props?.quoteUserReg && props?.quoteUserReg.coverage?.toString()) || '',
      period: (props as any)?.quoteUserReg?.period,
    },
  })
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (error instanceof AxiosError) {
      if (error.code === ApiErrorCode.CODE) {
        setSubmitError(MESSAGE_ENUM.ERROR_MESSAGE)
      }
    }
  }, [error, isError])

  const coverage = watch('coverage')
  const plan = cLookup[coverage]

  const hasMonthlyPremium = plan?.monthPremium !== null && plan?.monthPremium !== undefined

  useEffect(() => {
    if (!plan) return

    // If only one payment period is available, preselect it to avoid blocking the flow.
    if (!hasMonthlyPremium) setValue('period', Period.annual, { shouldValidate: true })
  }, [plan, hasMonthlyPremium, setValue])

  const onSubmit = async (data: FormData) => {
    if (!plan) return

    const obj = {
      ...props.quoteUserReg,
      coverageId: plan.id,
      period: data.period,
      coverage: data.coverage,
      planName: planInfo?.name,
    }
    props.setQuoteUserReg && props.setQuoteUserReg(obj)
    props?.setStage && props.setStage(Stages.STAGE4)
  }
  const chunkedAs = _.chunk(uniqueMaxCoverageRange, 4)

  return (
    <>
      {isLoading ? (
        <Flex justify="center" align="center" css={{ m: '6rem' }}>
          <Spinner />
        </Flex>
      ) : isError ? (
        submitError && (
          <>
            <Flex align="start" justify="center" css={QouteLayout}>
              <Flex justify="between" css={{ mb: 0 }}>
                <FormWrapper controlWidth={{ maxWidth: 700 }}>
                  <Flex as="h4" justify={'center'} css={{ ...userRegHeadingCss, color: '$error' }}>
                    {getUpperCase(submitError)}
                  </Flex>
                </FormWrapper>
              </Flex>
            </Flex>
          </>
        )
      ) : (
        <>
          <Flex align="start" justify="center" css={QouteLayout}>
            <Flex justify="between" css={{ mb: 0 }}></Flex>
            <FormWrapper controlWidth={{ maxWidth: 700 }}>
              <Flex align="center">
                <BackNavArrowIcon fontSize={24} style={{ cursor: 'pointer' }} onClick={() => props?.setStage && props.setStage(Stages.STAGE2)} />
                <Flex as="h2" justify={'center'} css={userRegHeadingCss}>
                  Get A Quote
                </Flex>
              </Flex>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <Box css={{ textAlign: 'left' }}>
                  <Box css={{ fontSize: '1rem', mb: '1rem' }}>Please Select Ransomware {TERMINOLOGY.benefit.singular}</Box>
                  <Flex direction={'row'} wrap={'wrap'} justify={'between'} css={{ maxWidth: '500px', width: '100%', columnGap: '20px' }}>
                    {chunkedAs?.map((row, rowIndex) => (
                      <Box key={rowIndex} css={{ flex: 1 }}>
                        {row.map((service: Service, index) => (
                          <Box key={index}>
                            <Label key={service.value} css={{ alignItems: 'center' }}>
                              <Controller
                                name="coverage"
                                control={control}
                                render={({ field }) => (
                                  <CustomRadioButton
                                    type="radio"
                                    value={service.value}
                                    checked={field.value === service.value}
                                    onChange={e => {
                                      field.onChange(e.target.value)
                                    }}
                                    data-state={field.value === service.value ? 'checked' : 'unchecked'}
                                  />
                                )}
                              />
                              <Box> {service.label}</Box>
                            </Label>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Flex>

                  {!!errors.coverage && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left' }}>{(errors.coverage as FieldError).message}</ErrorBox>}
                </Box>

                {plan && (
                  <div>
                    <Box css={{ fontSize: '1rem', mb: '1rem', textAlign: 'left' }}>Please Select Payment Period</Box>
                    <Flex direction={'row'} wrap={'wrap'} justify={'between'} css={{ maxWidth: '500px', width: '100%', columnGap: '20px' }}>
                      {hasMonthlyPremium && (
                        <Label css={{ flex: 1, alignItems: 'center' }}>
                          <Controller
                            name="period"
                            control={control}
                            render={({ field }) => (
                              <CustomRadioButton
                                type="radio"
                                value={Period.month}
                                checked={field.value === Period.month}
                                onChange={e => {
                                  field.onChange(e.target.value)
                                }}
                                data-state={field.value === Period.month ? 'checked' : 'unchecked'}
                              />
                            )}
                          />
                          <Box>Monthly (${plan.monthPremium?.toLocaleString()})</Box>
                        </Label>
                      )}

                      <Label css={{ flex: 1, alignItems: 'center' }}>
                        <Controller
                          name="period"
                          control={control}
                          render={({ field }) => (
                            <CustomRadioButton
                              type="radio"
                              value={Period.annual}
                              checked={field.value === Period.annual}
                              onChange={e => {
                                field.onChange(e.target.value)
                              }}
                              data-state={field.value === Period.annual ? 'checked' : 'unchecked'}
                            />
                          )}
                        />
                        <Box>Annual (${plan.annualPremium?.toLocaleString()})</Box>
                      </Label>
                    </Flex>
                    {!!errors.period && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left' }}>{(errors.period as FieldError).message}</ErrorBox>}
                  </div>
                )}

                <Box css={buttonCss}>
                  <Button bordered={true} type="button" onClick={() => navigate(routes.login)}>
                    Cancel
                  </Button>
                  <Button type="submit">Next</Button>
                </Box>
              </Form>
            </FormWrapper>
          </Flex>
        </>
      )}
    </>
  )
}

export const buttonCss = {
  'display': 'grid',
  'gridTemplateColumns': '1fr',
  'gap': 28,
  '@sm': {
    gridTemplateColumns: '1fr 1fr',
  },
}
export default CoveragePlan
